/**
 * THE ACTIVE VOICE SESSION — a module singleton, deliberately not React state.
 *
 * You stay in a voice room while you read a different channel, answer a chat
 * and open the files tab. A session owned by a route component would be torn
 * down by the first navigation, so the connection lives here and components
 * merely subscribe. There is exactly one session because you can only be in one
 * room at a time — the server enforces the same rule.
 */
import { useSyncExternalStore } from 'react';
import type { QualityProfile } from '@cyphex/types';
import type { ChannelId, ConversationId, TeamId } from '@cyphex/types/common';
import type { MediaClient, MediaSnapshot } from '@/features/meeting/media/contract';
import { createStore, useStoreValue } from '@/lib/tinyStore';

export type VoiceStatus = 'idle' | 'joining' | 'connected' | 'failed';

export interface VoiceSessionState {
  status: VoiceStatus;
  channelId: ChannelId | null;
  teamId: TeamId | null;
  conversationId: ConversationId | null;
  channelName: string;
  client: MediaClient | null;
  /** Local SFU identity. Matches `MediaParticipant.id` for the local tile. */
  identity: string;
  capacity: number;
  /** What admission control handed us at join. */
  assignedProfile: QualityProfile;
  /** What the degradation ladder settled on. */
  effectiveProfile: QualityProfile;
  /**
   * Plain-language reason the experience was reduced, or null. Shown to the
   * user verbatim: a call that silently drops video looks broken, a call that
   * says why looks reliable.
   */
  degradedReason: string | null;
  /** Mic capture is on at all (the big mute button). */
  micEnabled: boolean;
  /** Audio is actually reaching the room right now (push-to-talk / VAD gate). */
  transmitting: boolean;
  /** Local output kill switch. Implies not transmitting. */
  deafened: boolean;
  /**
   * The viewer asked to watch the room's screen share. Until they do, the share
   * is not subscribed at all — see session/voicePlan.ts.
   */
  watchingShare: boolean;
  error: string | null;
}

export const IDLE_SESSION: VoiceSessionState = {
  status: 'idle',
  channelId: null,
  teamId: null,
  conversationId: null,
  channelName: '',
  client: null,
  identity: '',
  capacity: 0,
  assignedProfile: 'standard',
  effectiveProfile: 'standard',
  degradedReason: null,
  micEnabled: true,
  transmitting: false,
  deafened: false,
  watchingShare: false,
  error: null,
};

export const voiceSessionStore = createStore<VoiceSessionState>(IDLE_SESSION);

export const patchSession = (patch: Partial<VoiceSessionState>): void => {
  voiceSessionStore.set((prev) => ({ ...prev, ...patch }));
};

export const useVoiceSession = (): VoiceSessionState =>
  useStoreValue(voiceSessionStore, (state) => state);

/** True when the caller is connected to (or joining) this specific channel. */
export const useIsInVoiceChannel = (channelId: ChannelId | undefined): boolean =>
  useStoreValue(
    voiceSessionStore,
    (state) => channelId !== undefined && state.channelId === channelId && state.status !== 'failed',
  );

/* Stable no-op sources, so `useSyncExternalStore` is called unconditionally
 * with referentially stable arguments even when there is no client yet. */
const NO_SUBSCRIBE = (): (() => void) => () => undefined;
const NO_SNAPSHOT = (): MediaSnapshot | null => null;

/**
 * The media snapshot of the active session, or null when not connected.
 *
 * Components read this instead of touching the client, so a session that ends
 * while a panel is mounted degrades to an empty panel rather than a crash.
 */
export function useVoiceSnapshot(): MediaSnapshot | null {
  const client = useStoreValue(voiceSessionStore, (state) => state.client);
  return useSyncExternalStore(
    client === null ? NO_SUBSCRIBE : client.subscribe,
    client === null ? NO_SNAPSHOT : client.getSnapshot,
    client === null ? NO_SNAPSHOT : client.getSnapshot,
  );
}
